import { TableCell } from "@mui/material"
import type { FC } from "react"
import { useFormContext } from "react-hook-form"

import { useInventoryDestinationFields } from "../../../../hooks"
import type { IntraTransferPalletSelectionFormValues } from "../../schemas/transfer"

import { FormAutocomplete } from "@/components/UiKit/FormControls/FormAutocomplete"
import { FormSelect } from "@/components/UiKit/FormControls/FormSelect"
import type { WarehouseArea } from "@/graphql/codegen/graphql"

const HOLD_AREA_COLUMN_COUNT = 1
const SHELVING_COLUMN_COUNT = 3

export const TRANSFER_PALLET_DESTINATION_COLUMN_COUNT = HOLD_AREA_COLUMN_COUNT + SHELVING_COLUMN_COUNT

interface IntraTransferPalletDestinationFieldsProps {
  index: number
  warehouseArea: WarehouseArea
  selectedShelvingIds: string[]
}

export const IntraTransferPalletDestinationFields: FC<IntraTransferPalletDestinationFieldsProps> = ({
  index,
  warehouseArea,
  selectedShelvingIds,
}) => {
  const { control, watch, setValue } = useFormContext<IntraTransferPalletSelectionFormValues>()

  const {
    fieldPaths,
    aisleCode,
    sectionCode,
    shouldShowShelvingFields,
    holdAreaOptions,
    aisleOptions,
    shelfOptions,
    binOptions,
    isFetching,
    isAisleFull,
    isSectionFull,
    handleHoldAreaChange,
    handleAisleCodeChange,
    handleSectionCodeChange,
    handleBinNumberChange,
  } = useInventoryDestinationFields<IntraTransferPalletSelectionFormValues>({
    name: `pallets.${index}.destination`,
    watch,
    setValue,
    warehouseArea,
    selectedShelvingIds,
  })

  return (
    <>
      <TableCell className='w-[220px] min-w-[220px] align-top'>
        <FormSelect
          control={control}
          name={fieldPaths.holdArea}
          placeholder='Select one'
          options={holdAreaOptions}
          onChange={handleHoldAreaChange}
          data-testid={`transfer.pallet-destination-fields.area-field-${index}`}
        />
      </TableCell>
      {shouldShowShelvingFields ? (
        <>
          <TableCell className='w-[140px] min-w-[140px] align-top'>
            <FormAutocomplete
              control={control}
              name={fieldPaths.aisleCode}
              placeholder='Select one'
              options={aisleOptions}
              disabled={isFetching}
              noOptionsText='No aisles'
              error={isAisleFull}
              helperText={isAisleFull ? "No available bins in this aisle" : undefined}
              onChange={handleAisleCodeChange}
              data-testid={`transfer.pallet-destination-fields.aisle-field-${index}`}
            />
          </TableCell>
          <TableCell className='w-[140px] min-w-[140px] align-top'>
            <FormAutocomplete
              control={control}
              name={fieldPaths.sectionCode}
              placeholder='Select one'
              options={shelfOptions}
              disabled={!aisleCode || isFetching}
              noOptionsText='No shelves'
              error={isSectionFull}
              helperText={isSectionFull ? "No available bins on this shelf" : undefined}
              onChange={handleSectionCodeChange}
              data-testid={`transfer.pallet-destination-fields.shelf-field-${index}`}
            />
          </TableCell>
          <TableCell className='w-[140px] min-w-[140px] align-top'>
            <FormAutocomplete
              control={control}
              name={fieldPaths.binNumber}
              placeholder='Select one'
              options={binOptions}
              disabled={!sectionCode || isFetching}
              noOptionsText='No bins'
              onChange={handleBinNumberChange}
              data-testid={`transfer.pallet-destination-fields.bin-field-${index}`}
            />
          </TableCell>
        </>
      ) : (
        <TableCell colSpan={SHELVING_COLUMN_COUNT} />
      )}
    </>
  )
}
